import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { EXTRACTION_STEPS_API_CONFIG } from "./extraction-steps-api-config";

/** Extraction step — a discriminated union by extraction_type. */
interface ExtractionStep {
	id: string;
	specification_id: string;
	step_order: number;
	extraction_type: string;
	particle_collection_mode: string;
	collector_type_id?: string;
	created_at: string;
	// Discriminated properties
	liquid_type?: string;
	liquid_volume_ml?: number;
	duration_s?: number;
	pressure_bar?: number;
	nozzle_dimension_mm?: number;
	volume_flow_l_min?: number;
	amount_flushing?: string;
	frequency_khz?: number;
	power_w?: number;
	agitation_frequency_hz?: number;
	air_pressure_bar?: number;
	distance_mm?: number;
	suction_nozzle_type?: string;
	suction_area_mm2?: number;
	brush_attachment?: boolean;
	suction_volume_flow_l_min?: number;
	suction_distance_mm?: number;
	suction_duration_s?: number;
	separation_cyclone?: boolean;
	separation_carrier?: string;
	stamp_type?: string;
	stamp_design?: string;
	stamp_area_cm2?: number;
	stamp_count?: number;
	stamp_imprints?: number;
}

/** Input for creating an extraction step. */
interface CreateExtractionStepDto {
	specification_id: string;
	step_order: number;
	extraction_type: string;
	particle_collection_mode: string;
	collector_type_id?: string;
	// Plus discriminated properties — open for extension.
	[key: string]: unknown;
}

/** Input for updating an extraction step. */
type UpdateExtractionStepDto = Partial<CreateExtractionStepDto>;

/**
 * Data-access service for the extraction steps API.
 *
 * Targets the normalised `ExtractionStep` (discriminated-union) entity model.
 * All calls are authenticated by `portalApiInterceptor`.
 */
@Injectable({
	providedIn: "root",
})
export class ExtractionStepsDataService {
	private readonly config = inject(EXTRACTION_STEPS_API_CONFIG);

	constructor(private readonly http: HttpClient) {}

	private get baseUrl(): string {
		return `${this.config.portalBackendUrl ?? "http://localhost:3000"}/api`;
	}

	/** GET /api/extraction-steps -- list steps, optional specification_id filter */
	getExtractionSteps(specificationId?: string): Observable<ExtractionStep[]> {
		const params = specificationId ? new HttpParams().set("specification_id", specificationId) : undefined;
		return this.http.get<ExtractionStep[]>(`${this.baseUrl}/extraction-steps`, { params });
	}

	/** GET /api/extraction-steps/:id -- single extraction step */
	getExtractionStep(id: string): Observable<ExtractionStep> {
		return this.http.get<ExtractionStep>(`${this.baseUrl}/extraction-steps/${id}`);
	}

	/** POST /api/extraction-steps -- create extraction step (discriminated by extraction_type) */
	createExtractionStep(dto: CreateExtractionStepDto): Observable<ExtractionStep> {
		return this.http.post<ExtractionStep>(`${this.baseUrl}/extraction-steps`, dto);
	}

	/** PUT /api/extraction-steps/:id -- update extraction step */
	updateExtractionStep(id: string, dto: UpdateExtractionStepDto): Observable<ExtractionStep> {
		return this.http.put<ExtractionStep>(`${this.baseUrl}/extraction-steps/${id}`, dto);
	}

	/** DELETE /api/extraction-steps/:id -- delete extraction step */
	deleteExtractionStep(id: string): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/extraction-steps/${id}`);
	}
}
